export type Lang = 'de' | 'en';

export interface Strings {
  langName: string;
  switchTo: string;
  nav: { privacy: string; imprint: string; allTools: string };
  hero: { title: string; subtitle: string; localBadge: string };
  ui: {
    name: string;
    namePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    passphrase: string;
    passphraseHint: string;
    generate: string;
    keyId: string;
    publicKey: string;
    privateKey: string;
    download: string;
    securityTitle: string;
    securityBody: string;
    privateHint: string;
    error: string;
  };
  seo: {
    aboutTitle: string;
    aboutBody: string;
    faqTitle: string;
    faq: { q: string; a: string }[];
  };
  footer: { rights: string; noStorage: string };
}

const de: Strings = {
  langName: 'Deutsch',
  switchTo: 'English',
  nav: { privacy: 'Datenschutz', imprint: 'Impressum', allTools: 'Alle Tools' },
  hero: {
    title: 'PGP-Key-Generator',
    subtitle:
      'Erzeuge ein OpenPGP-Schlüsselpaar (Ed25519) direkt im Browser. Der private Schlüssel wird passphrasen-verschlüsselt und verlässt dein Gerät nie.',
    localBadge: '100 % lokal · kostenlos',
  },
  ui: {
    name: 'Name',
    namePlaceholder: 'Vorname Nachname',
    email: 'E-Mail (optional)',
    emailPlaceholder: 'name@example.com',
    passphrase: 'Passphrase',
    passphraseHint: 'Schützt den privaten Schlüssel. Merke sie dir gut — sie ist nicht wiederherstellbar.',
    generate: 'Schlüsselpaar erzeugen',
    keyId: 'Key-ID',
    publicKey: 'Öffentlicher Schlüssel',
    privateKey: 'Privater Schlüssel',
    download: 'Herunterladen',
    securityTitle: 'Dein Schlüssel bleibt bei dir',
    securityBody:
      'Die Erzeugung läuft vollständig in deinem Browser. Der private Schlüssel wird mit deiner Passphrase verschlüsselt und niemals hochgeladen oder gespeichert.',
    privateHint:
      'Bewahre den privaten Schlüssel und die Passphrase sicher auf. Ohne Passphrase ist der Schlüssel unbrauchbar.',
    error: 'Schlüsselerzeugung fehlgeschlagen. Bitte erneut versuchen.',
  },
  seo: {
    aboutTitle: 'PGP-Schlüssel sicher und lokal erzeugen',
    aboutBody:
      'Dieser Generator erzeugt OpenPGP-Schlüsselpaare (Ed25519) ausschließlich lokal in deinem Browser. Der private Schlüssel wird passphrasen-verschlüsselt und nie übertragen oder gespeichert.',
    faqTitle: 'Häufige Fragen',
    faq: [
      {
        q: 'Wird mein privater Schlüssel hochgeladen?',
        a: 'Nein. Die Erzeugung läuft vollständig lokal im Browser. Der private Schlüssel wird mit deiner Passphrase verschlüsselt und nicht übertragen.',
      },
      {
        q: 'Welcher Algorithmus wird verwendet?',
        a: 'Ed25519 (EdDSA) mit ECDH-Unterschlüssel gemäß RFC 4880/6637 — modern und sicher.',
      },
      {
        q: 'Wofür nutze ich PGP-Schlüssel?',
        a: 'Zum Verschlüsseln und Signieren von E-Mails und Dateien (z. B. mit GnuPG).',
      },
    ],
  },
  footer: {
    rights: 'Kostenlose Tools von ecosquirrel.',
    noStorage: 'Keine serverseitige Speicherung. Dein privater Schlüssel verlässt den Browser nie.',
  },
};

const en: Strings = {
  langName: 'English',
  switchTo: 'Deutsch',
  nav: { privacy: 'Privacy', imprint: 'Imprint', allTools: 'All tools' },
  hero: {
    title: 'PGP Key Generator',
    subtitle:
      'Generate an OpenPGP key pair (Ed25519) right in your browser. The private key is passphrase-encrypted and never leaves your device.',
    localBadge: '100% local · free',
  },
  ui: {
    name: 'Name',
    namePlaceholder: 'First Last',
    email: 'Email (optional)',
    emailPlaceholder: 'name@example.com',
    passphrase: 'Passphrase',
    passphraseHint: 'Protects the private key. Remember it well — it cannot be recovered.',
    generate: 'Generate key pair',
    keyId: 'Key ID',
    publicKey: 'Public key',
    privateKey: 'Private key',
    download: 'Download',
    securityTitle: 'Your key stays with you',
    securityBody:
      'Generation runs entirely in your browser. The private key is encrypted with your passphrase and never uploaded or stored.',
    privateHint:
      'Keep the private key and passphrase safe. Without the passphrase the key is unusable.',
    error: 'Key generation failed. Please try again.',
  },
  seo: {
    aboutTitle: 'Generate PGP keys securely and locally',
    aboutBody:
      'This generator creates OpenPGP key pairs (Ed25519) exclusively in your browser. The private key is passphrase-encrypted and never transmitted or stored.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        q: 'Is my private key uploaded?',
        a: 'No. Generation runs entirely in the browser. The private key is encrypted with your passphrase and not transmitted.',
      },
      {
        q: 'Which algorithm is used?',
        a: 'Ed25519 (EdDSA) with an ECDH subkey per RFC 4880/6637 — modern and secure.',
      },
      {
        q: 'What are PGP keys for?',
        a: 'Encrypting and signing emails and files (e.g. with GnuPG).',
      },
    ],
  },
  footer: {
    rights: 'Free tools by ecosquirrel.',
    noStorage: 'No server-side storage. Your private key never leaves the browser.',
  },
};

export const STRINGS: Record<Lang, Strings> = { de, en };
